import { ChatUiAdapter } from './app/chat-ui-adapter';
import { toExtremeFeatureRows, toExtremeRunRows, toLeaderboardRows } from './app/leaderboard-adapter';
import { toAppViewModel } from './app/view-model';
import { createGameCycleJournal } from './game/cycle-codecs';
import { GameController, type GameIdPurpose } from './game/game-controller';
import type { GamePhase, GameViewModel } from './game/runtime-state';
import { CryptoRandomSource, SequenceRandomSource } from './core/random';
import { REEL_STRIPS } from './core/reel-strips';
import { createGridFromStops } from './core/reels';
import type { RandomSource, ReelStops } from './core/types';
import type { LeaderboardBoard, LeaderboardEntry, LeaderboardSeasonId } from './leaderboard/types';
import type { PresentationEvent, PresentationEventSink } from './presentation/events';
import { getWinTier } from './presentation/win-tier';
import {
  adaptReplayHeader,
  adaptReplayListItem,
  adaptStoredReplayStep,
} from './presentation/replay-adapter';
import { ReplayPresentationController } from './presentation/replay-presentation';
import { TempleSlotRenderer } from './rendering/slot-renderer';
import type { ReplayPresentationStep } from './rendering/types';
import { completeReplay } from './replay/recorder';
import type { FreeSpinReplayV2 } from './replay/schema';
import {
  AudioManager,
  loadAudioPreferences,
  type AudioCue,
} from './services/audio';
import { ChatService } from './services/chat';
import { FirebaseRealtimeDatabase } from './services/firebase';
import { LeaderboardService } from './services/leaderboard';
import { PersistenceService } from './services/persistence';
import { LocalFirstReplayRepository } from './services/replay';
import { SeasonArchiveService, type SeasonArchive } from './services/season-archive';
import { browserStorage, MemoryStorage, type StoragePort } from './services/storage';
import {
  createAppShell,
  FeatureDialogController,
  LeaderboardView,
  ReplayDialogView,
  type AppShellPort,
} from './ui';
import { animateCentCount } from './ui/count-up';
import { ExtremeLocalService } from './extreme/local-service';
import { EXTREME_PAID_SPINS } from './extreme/types';

const INITIAL_STOPS = [0, 0, 0, 0, 0] as const satisfies ReelStops;
const BASE_SCENARIO_SEQUENCE = [0.001, 0.001, 0.001, 0.001, 0.001] as const;
const FEATURE_SCENARIO_SEQUENCE = [
  7.1 / 58, 9.1 / 58, 11.1 / 58, 0.1 / 58, 0.1 / 58,
  22.1 / 58, 25.1 / 58, 27.1 / 58, 24.1 / 58, 26.1 / 58,
  0.001, 0,
] as const;

function requireElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (element === null) throw new Error(`Bootstrap-Element fehlt: ${selector}`);
  return element;
}

function createRuntimeRandom(): RandomSource {
  if (import.meta.env.DEV) {
    const scenario = new URL(globalThis.location.href).searchParams.get('scenario');
    if (scenario === 'base') return new SequenceRandomSource(BASE_SCENARIO_SEQUENCE);
    if (scenario === 'feature') return new SequenceRandomSource(FEATURE_SCENARIO_SEQUENCE);
  }
  return new CryptoRandomSource();
}

function createRuntimeId(purpose: GameIdPurpose): string {
  const value = globalThis.crypto.randomUUID();
  return `${purpose}-${value}`;
}

function leaderboardEntry(input: {
  readonly id: string;
  readonly name: string;
  readonly profitCents: number;
  readonly betCents: number;
  readonly board: LeaderboardBoard;
  readonly replayAvailable?: boolean;
}): LeaderboardEntry {
  return Object.freeze({
    id: input.id,
    clientEntryId: input.id,
    name: input.name,
    profitCents: input.profitCents,
    betCents: input.betCents,
    createdAt: Date.now(),
    seasonId: 'season3',
    board: input.board,
    ...(input.replayAvailable === undefined ? {} : { replayAvailable: input.replayAvailable }),
  });
}

async function bootstrap(): Promise<void> {
  const storage: StoragePort = browserStorage() ?? new MemoryStorage();
  const browserLocalStorage = browserStorage();
  const persistence = new PersistenceService(storage);
  const firebase = new FirebaseRealtimeDatabase();
  const chatService = new ChatService(firebase);
  const leaderboardService = new LeaderboardService(storage, firebase);
  const extremeService = new ExtremeLocalService(storage, firebase);
  const replayRepository = new LocalFirstReplayRepository(storage, firebase);
  const seasonArchive = new SeasonArchiveService();
  const audio = new AudioManager({
    ...(browserLocalStorage === null ? {} : { initialPreferences: loadAudioPreferences(browserLocalStorage as Storage) }),
  });
  const detachAudioUnlock = audio.attachUnlock(document);
  let shell: AppShellPort | null = null;
  let controller: GameController | null = null;
  let activeReplay: FreeSpinReplayV2 | null = null;
  let replayPresentation: ReplayPresentationController | null = null;
  let replayRenderToken = 0;
  let replayRenderer: TempleSlotRenderer | null = null;
  const seasonArchives = new Map<'season1' | 'season2', SeasonArchive>();
  let lastLeaderboardSeason: LeaderboardSeasonId = 'season3';
  let lastLeaderboardBoard: LeaderboardBoard = 'runs';
  let lastLeaderboardMode: 'ranked' | 'extreme' = 'ranked';
  let leaderboardRequestId = 0;
  let baseMusicStarted = false;
  let latestGameView: GameViewModel | null = null;

  const ensureBaseMusic = async (): Promise<void> => {
    // Beide Aufrufe beginnen synchron innerhalb derselben echten Geste. Das ist
    // auf iOS zuverlässiger als erst auf ein resume()-Promise zu warten und
    // MediaElement.play() danach außerhalb des Gesture-Stacks aufzurufen.
    const unlock = audio.unlock();
    if (baseMusicStarted) {
      await unlock;
      return;
    }
    baseMusicStarted = true;
    const music = audio.startBaseMusic();
    await Promise.all([unlock, music]);
  };

  const presentationSink: PresentationEventSink = Object.freeze({
    emit(event: PresentationEvent): void {
      audio.emit(event);
      if (event.type === 'retrigger') {
        shell?.setStatus(`Retrigger: +${event.awarded} Freispiele`, 'bonus');
      }
      if (event.type === 'pot-awarded') {
        shell?.showPotAward(event.awarded);
        shell?.setStatus(`Topf der Gier: +${event.awarded} Freispiel`, 'bonus');
      }
      if (event.type === 'presentation-complete' && latestGameView !== null) {
        const cue: AudioCue | null = getWinTier(latestGameView.lastWinCents, latestGameView.betCents);
        if (cue !== null) audio.playCue(cue, { duckMusicMs: cue === 'jackpot' ? 900 : 420 });
      }
    },
  });

  const renderer = new TempleSlotRenderer({
    eventSink: presentationSink,
    reducedMotion: globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches,
    mode: 'cards',
  });
  await renderer.initialize(
    requireElement<HTMLElement>('[data-render-host]'),
    createGridFromStops(REEL_STRIPS, INITIAL_STOPS),
  );

  const chatUi = new ChatUiAdapter(chatService, firebase);
  const leaderboardView = new LeaderboardView(document);
  const replayView = new ReplayDialogView(document);
  const featureDialogs = new FeatureDialogController(document, {
    reducedMotion: globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches,
  });
  const renderLeaderboard = async (
    season: LeaderboardSeasonId,
    mode: 'ranked' | 'extreme',
    board: LeaderboardBoard,
  ): Promise<void> => {
    const requestId = ++leaderboardRequestId;
    const isCurrentRequest = (): boolean => requestId === leaderboardRequestId;
    lastLeaderboardSeason = season;
    lastLeaderboardMode = mode;
    lastLeaderboardBoard = board;
    leaderboardView.configure(mode, board);
    leaderboardView.renderStatus({ state: 'loading', message: 'Rangliste wird geladen …', source: season === 'season3' ? 'local' : 'archive' });
    try {
      if (mode === 'extreme') {
        if (season !== 'season3') {
          leaderboardView.renderRows([], { archived: true });
          leaderboardView.renderStatus({ state: 'ready', message: 'Extreme ist ab Season 3 verfügbar.', source: 'archive' });
          return;
        }
        const sync = await extremeService.flushPending();
        if (board === 'runs') {
          const entries = await extremeService.refreshRuns();
          if (!isCurrentRequest()) return;
          leaderboardView.renderRows(toExtremeRunRows(entries));
        } else {
          const entries = await extremeService.refreshFeatures();
          if (!isCurrentRequest()) return;
          leaderboardView.renderRows(toExtremeFeatureRows(entries));
        }
        const remote = firebase.availability.status === 'online' && sync.pending === 0;
        leaderboardView.renderStatus({
          state: remote ? 'ready' : 'fallback',
          message: remote
            ? 'Extreme · global bestätigt'
            : sync.pending > 0
              ? `Extreme · ${sync.pending} Upload${sync.pending === 1 ? '' : 's'} vorgemerkt`
              : 'Extreme · lokaler Offline-Stand',
          source: remote ? 'firebase' : 'local',
        });
        return;
      }
      if (season !== 'season3') {
        const archive = seasonArchives.get(season) ?? await seasonArchive.load(season);
        if (!isCurrentRequest()) return;
        seasonArchives.set(season, archive);
        const entries = board === 'runs' ? archive.runs : archive.freeSpins;
        leaderboardView.renderRows(toLeaderboardRows(entries, new Set(archive.replays.keys())), { archived: true });
        leaderboardView.renderStatus({ state: 'ready', message: `${archive.label} · unveränderliches Archiv`, source: 'archive' });
        return;
      }
      const entries = await leaderboardService.refresh(board);
      if (!isCurrentRequest()) return;
      const replayIds = board === 'freeSpins'
        ? new Set(entries.map((entry) => entry.clientEntryId))
        : new Set<string>();
      leaderboardView.renderRows(toLeaderboardRows(entries, replayIds));
      const remote = firebase.availability.status === 'online';
      leaderboardView.renderStatus({
        state: remote ? 'ready' : 'fallback',
        message: remote ? 'Season 3 · global bestätigt' : 'Season 3 · lokaler Offline-Stand',
        source: remote ? 'firebase' : 'local',
      });
      if (remote) {
        await Promise.all([leaderboardService.flushPending(), replayRepository.flushPending()]);
      }
    } catch {
      if (!isCurrentRequest()) return;
      leaderboardView.renderRows(mode === 'extreme'
        ? board === 'runs'
          ? toExtremeRunRows(extremeService.localRuns())
          : toExtremeFeatureRows(extremeService.localFeatures())
        : toLeaderboardRows(leaderboardService.local(board)));
      leaderboardView.renderStatus({ state: 'fallback', message: 'Online nicht erreichbar · lokaler Stand', source: 'local' });
    }
  };

  const openReplay = async (replayId: string): Promise<void> => {
    let replay: FreeSpinReplayV2 | null = null;
    if (lastLeaderboardMode === 'extreme') {
      replay = await extremeService.getFeatureReplay(replayId);
    } else if (lastLeaderboardSeason !== 'season3') {
      const archive = seasonArchives.get(lastLeaderboardSeason) ?? await seasonArchive.load(lastLeaderboardSeason);
      seasonArchives.set(lastLeaderboardSeason, archive);
      replay = archive.replays.get(replayId) ?? null;
    } else {
      replay = await replayRepository.get(replayId);
    }
    if (replay === null) {
      shell?.setStatus('Dieses Replay ist derzeit nicht verfügbar.', 'error');
      return;
    }
    activeReplay = replay;
    replayPresentation?.destroy();
    const steps = replay.steps.map(adaptStoredReplayStep);
    const firstStep = steps[0];
    if (firstStep === undefined) {
      shell?.setStatus('Dieses Replay enthält keine gültigen Schritte.', 'error');
      return;
    }
    if (replayRenderer === null) {
      replayRenderer = new TempleSlotRenderer({
        reducedMotion: globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches,
        mode: replay.presentationMode,
      });
      await replayRenderer.initialize(
        requireElement<HTMLElement>('[data-replay-render-host]'),
        firstStep.grid,
      );
    } else {
      replayRenderer.setMode(replay.presentationMode);
    }
    const selectedReplayRenderer = replayRenderer;
    replayPresentation = new ReplayPresentationController(steps, {
      present(step: ReplayPresentationStep, index: number, total: number): void {
        const token = ++replayRenderToken;
        replayView.setBusy(true);
        void selectedReplayRenderer.presentReplayStep(step).then(() => {
          if (token !== replayRenderToken) return;
          replayView.renderStep(step, index, total, replayPresentation?.isAutoplaying === true);
          replayView.setBusy(false);
        }).catch(() => {
          if (token !== replayRenderToken) return;
          replayView.setBusy(false);
          shell?.setStatus('Replay-Schritt konnte nicht dargestellt werden.', 'error');
        });
      },
    });
    const activeArchive = lastLeaderboardSeason === 'season3' ? null : seasonArchives.get(lastLeaderboardSeason);
    const availableReplays = lastLeaderboardMode === 'extreme'
      ? extremeService.localFeatureReplays()
      : activeArchive === undefined || activeArchive === null
        ? replayRepository.list()
        : [...activeArchive.replays.values()];
    replayView.renderList(availableReplays.map(adaptReplayListItem), replay.id);
    replayView.show(adaptReplayHeader(replay));
    replayPresentation.show(0);
  };

  leaderboardView.setReplayHandler((entryId) => {
    void openReplay(entryId);
  });

  const closeFeatureSummary = async (): Promise<void> => {
    if (controller === null) return;
    const ranked = controller.viewModel.rankedRun;
    const completed = controller.closeSummary();
    await audio.startBaseMusic({ randomStart: true });
    if (completed?.replayDraft === null || completed?.replayDraft === undefined || ranked === null) return;
    const replay = completeReplay(completed.replayDraft, {
      clientEntryId: completed.replayDraft.id,
      createdAt: Date.now(),
    });
    await replayRepository.save(replay);
    const result = await leaderboardService.submit(leaderboardEntry({
      id: replay.clientEntryId,
      name: ranked.username,
      profitCents: replay.totalWinCents,
      betCents: ranked.betCents,
      board: 'freeSpins',
      replayAvailable: true,
    }));
    shell?.setStatus(
      result.remoteConfirmed && result.globalRank !== null
        ? `Freispielrang global bestätigt: Platz ${result.globalRank}`
        : `Freispielergebnis lokal gespeichert${result.queued ? ' · Upload vorgemerkt' : ''}`,
      'bonus',
    );
  };

  shell = createAppShell(document, {
    // Audio darf nie den Spielzyklus verzögern. Insbesondere auf iOS kann
    // AudioContext-Resume oder Sample-Decoding asynchron nachlaufen; der
    // eigentliche Aufruf wird trotzdem noch synchron aus derselben Geste
    // angestoßen, damit der Browser ihn zuverlässig freigibt.
    onSpin: async () => {
      void ensureBaseMusic();
      await controller?.spin();
    },
    onAutoChange: (enabled) => { void ensureBaseMusic(); controller?.setAutoEnabled(enabled); },
    onTurboChange: (enabled) => controller?.setTurbo(enabled),
    onBetChange: (betCents) => { controller?.setBet(betCents); },
    onModeRequest: () => undefined,
    onRankedStart: async (playerName) => {
      await ensureBaseMusic();
      const run = controller?.startRanked({ username: playerName });
      if (run !== null && run !== undefined) shell?.closeDialog('ranked');
    },
    onRankedEnd: async () => {
      const completed = controller?.finishRanked();
      if (completed === null || completed === undefined) return;
      const result = await leaderboardService.submit(leaderboardEntry({
        id: completed.id,
        name: completed.username,
        profitCents: completed.profitCents,
        betCents: completed.betCents,
        board: 'runs',
      }));
      shell?.setStatus(
        result.remoteConfirmed && result.globalRank !== null
          ? `Run global bestätigt: Platz ${result.globalRank}`
          : `Run lokal gespeichert${result.queued ? ' · Upload vorgemerkt' : ''}`,
        'win',
      );
    },
    onExtremeStart: async (playerName) => {
      await ensureBaseMusic();
      const run = controller?.startExtreme({ username: playerName });
      if (run !== null && run !== undefined) {
        shell?.closeDialog('ranked');
        shell?.setStatus(`Extreme-Run gestartet · ${EXTREME_PAID_SPINS} Basisspins verbleiben.`, 'bonus');
      }
    },
    onExtremeAbandon: () => {
      if (controller?.abandonExtreme()) shell?.setStatus('Extreme-Run ungültig beendet.', 'neutral');
    },
    onExtremeComplete: async () => {
      const completed = controller?.finishExtreme();
      if (completed === null || completed === undefined) return;
      const result = extremeService.completeRun(completed);
      shell?.closeDialog('extreme-summary');
      const sync = await extremeService.flushPending();
      const globalRuns = sync.pending === 0 ? await extremeService.refreshRuns() : [];
      const globalRankIndex = globalRuns.findIndex((entry) => entry.id === result.run.id);
      shell?.setStatus(
        sync.pending === 0
          ? globalRankIndex >= 0
            ? `Extreme-Run global bestätigt · Platz ${globalRankIndex + 1}`
            : 'Extreme-Run global bestätigt.'
          : result.localRunRank === null || result.localRunRank > 10
            ? 'Extreme-Run lokal gespeichert · Upload vorgemerkt.'
            : `Extreme-Run lokal gespeichert · Platz ${result.localRunRank} · Upload vorgemerkt`,
        'win',
      );
    },
    onAutoStopChange: (enabled, threshold) => controller?.configureAutoStop(enabled, threshold),
    onLeaderboardOpen: () => renderLeaderboard('season3', 'ranked', 'runs'),
    onLeaderboardViewChange: renderLeaderboard,
    onReplayAction: (action) => {
      if (action === 'previous') replayPresentation?.previous();
      if (action === 'next') replayPresentation?.next();
      if (action === 'toggle-auto') {
        if (replayPresentation?.isAutoplaying === true) {
          replayPresentation.stop();
          replayPresentation.show();
        } else replayPresentation?.start();
      }
      if (action === 'close') {
        replayPresentation?.stop();
        replayRenderer?.cancel();
        activeReplay = null;
      }
    },
    onReplaySelect: openReplay,
  }, {
    audio,
    chat: chatUi,
    ...(browserLocalStorage === null ? {} : { storage: browserLocalStorage as Storage }),
  });

  controller = new GameController({
    renderer,
    persistence,
    journal: createGameCycleJournal(storage),
    random: createRuntimeRandom(),
    createId: createRuntimeId,
    reducedMotion: globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches,
  });

  let lastPhase: GamePhase | null = null;
  let displayedBalance = controller.viewModel.balanceCents;
  let displayedLastWin = 0;
  let countToken = 0;
  const unsubscribe = controller.subscribe((view) => {
    latestGameView = view;
    const appView = toAppViewModel(view);
    if (view.phase === 'spinning' || view.phase === 'recovering') {
      displayedBalance = Math.min(displayedBalance, view.balanceCents);
      shell?.render({ ...appView, balanceCents: displayedBalance, lastWinCents: displayedLastWin });
    } else {
      const fromBalance = displayedBalance;
      const toBalance = view.balanceCents;
      const fromWin = displayedLastWin;
      const toWin = view.lastWinCents;
      const token = ++countToken;
      shell?.render({ ...appView, balanceCents: fromBalance, lastWinCents: fromWin });
      const durationMs = view.turbo ? 120 : 420;
      void Promise.all([
        animateCentCount({
          fromCents: fromBalance,
          toCents: toBalance,
          durationMs,
          reducedMotion: globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches,
          onUpdate: (balanceCents) => {
            if (token !== countToken) return;
            displayedBalance = balanceCents;
            shell?.render({ ...toAppViewModel(view), balanceCents, lastWinCents: displayedLastWin });
          },
        }),
        animateCentCount({
          fromCents: fromWin,
          toCents: toWin,
          durationMs,
          reducedMotion: globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches,
          onUpdate: (lastWinCents) => {
            if (token !== countToken) return;
            displayedLastWin = lastWinCents;
          },
        }),
      ]).then(() => {
        if (token !== countToken) return;
        displayedBalance = toBalance;
        displayedLastWin = toWin;
        shell?.render({ ...toAppViewModel(view), balanceCents: toBalance, lastWinCents: toWin });
      });
    }

    if (view.phase === lastPhase) return;
    lastPhase = view.phase;
    if (view.phase === 'feature-intro' && view.bonus !== null) {
      void audio.fadeOutMusic();
      audio.playCue('feature', { duckMusicMs: 650 });
      void featureDialogs.showIntro(view.bonus.totalAwardedSpins).then(async () => {
        await audio.startFeatureMusic();
        controller?.startFeature();
      });
    }
    if (view.phase === 'feature-summary' && view.featureSummary !== null) {
      void featureDialogs.showSummary(view.featureSummary.totalWinCents, view.betCents)
        .then(closeFeatureSummary)
        .catch(() => shell?.setStatus('Featureabschluss konnte nicht gespeichert werden.', 'error'));
    }
    if (view.phase === 'extreme-summary' && view.extremeRun !== null) {
      controller?.setAutoEnabled(false);
      shell?.showDialog('extreme-summary');
    }
  });

  document.addEventListener('pointerdown', () => { void ensureBaseMusic(); }, { once: true, passive: true });

  if (controller.viewModel.phase === 'recovering') {
    await controller.recover();
  }

  if (import.meta.env.DEV) {
    Object.assign(globalThis, {
      __YGO_DEV__: Object.freeze({
        getView: () => controller?.viewModel ?? null,
        openReplay: (id: string) => openReplay(id),
        getActiveReplay: () => activeReplay,
        getLeaderboardSelection: () => ({ season: lastLeaderboardSeason, mode: lastLeaderboardMode, board: lastLeaderboardBoard }),
      }),
    });
  }

  const shutdown = (): void => {
    unsubscribe();
    replayPresentation?.destroy();
    replayRenderer?.destroy();
    replayRenderer = null;
    featureDialogs.destroy();
    shell?.destroy();
    controller?.destroy();
    detachAudioUnlock();
    audio.destroy();
    renderer.destroy();
  };
  globalThis.addEventListener('pagehide', shutdown, { once: true });
}

void bootstrap().catch((error: unknown) => {
  const message = error instanceof Error ? error.message : 'Unbekannter Startfehler';
  const loading = document.querySelector<HTMLElement>('[data-render-loading]');
  if (loading !== null) loading.textContent = `Der Tempel konnte nicht geöffnet werden: ${message}`;
  const status = document.querySelector<HTMLElement>('[data-status-text]');
  if (status !== null) status.textContent = 'Sicherer Fehlerzustand – bitte Seite neu laden.';
});
